import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Trash2, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";
import NovaCriteriaRadar from "./NovaCriteriaRadar";
import NovaVotesHistory from "./NovaVotesHistory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NovaVotingPanel — 6-criterion voting UI for a challenge in the "comité" phase.
 * Props:
 *   challenge: { id, committee_votes, committee_score }
 *   criteria:  campaign.voting_criteria (array of {key,label,weight,description})
 *   token
 *   onSaved:  callback after submit/delete
 */
export default function NovaVotingPanel({ challenge, criteria, token, onSaved }) {
  const myVote = (challenge.committee_votes || []).find((v) => v.__mine) || null;
  const [scores, setScores] = useState(() => {
    const base = {};
    criteria.forEach((c) => { base[c.key] = (myVote?.scores?.[c.key] ?? 3); });
    return base;
  });
  const [comment, setComment] = useState(myVote?.comment || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/challenges/${challenge.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scores, comment }),
      });
      if (!r.ok) throw new Error();
      toast.success("Voto guardado");
      onSaved && onSaved();
    } catch {
      toast.error("Error guardando voto");
    } finally { setSaving(false); }
  };

  const removeVote = async () => {
    if (!window.confirm("¿Eliminar tu voto?")) return;
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/nova/challenges/${challenge.id}/vote`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Voto eliminado");
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const score = challenge.committee_score || { count: 0, overall: 0, per_criterion: {} };

  return (
    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-500/5 p-3 space-y-3" data-testid={`voting-panel-${challenge.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-300">Votación Comité · 6 criterios</div>
        <div className="text-xs text-muted-foreground">
          <strong className="text-emerald-700 dark:text-emerald-300">{score.overall}/5</strong> · {score.count} votos
        </div>
      </div>
      {score.count > 0 && (
        <NovaCriteriaRadar score={score} criteria={criteria} height={200}/>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {criteria.map((c) => {
          const val = scores[c.key] ?? 3;
          const avg = score.per_criterion?.[c.key]?.avg;
          const scaleHint = c.scale?.[String(val)];
          const tooltipLines = [c.description];
          if (c.scale) {
            tooltipLines.push("");
            for (let i = 0; i <= 5; i++) if (c.scale[String(i)]) tooltipLines.push(`${i} · ${c.scale[String(i)]}`);
          }
          return (
            <div key={c.key} className="space-y-1" title={tooltipLines.join("\n")}>
              <div className="flex items-center justify-between text-xs">
                <label className="font-semibold text-slate-700 dark:text-slate-200">{c.label}</label>
                <div className="flex items-center gap-2">
                  {avg > 0 && <span className="text-[10px] text-muted-foreground">avg {avg}</span>}
                  <span className="font-bold text-emerald-700 dark:text-emerald-300 w-6 text-right">{val}</span>
                </div>
              </div>
              <input
                type="range" min="0" max="5" step="1"
                value={val}
                onChange={(e) => setScores({ ...scores, [c.key]: Number(e.target.value) })}
                className="w-full accent-emerald-500"
                data-testid={`vote-slider-${challenge.id}-${c.key}`}
              />
              {scaleHint && (
                <div className="text-[10px] text-emerald-700 dark:text-emerald-300 italic truncate" title={scaleHint} data-testid={`vote-scale-hint-${challenge.id}-${c.key}`}>
                  {val} · {scaleHint}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-muted-foreground flex items-center gap-1"><MessageSquare className="w-3 h-3"/> Comentario</label>
        <textarea
          rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Argumenta tu valoración (opcional)…"
          className="w-full mt-1 px-2 py-1 rounded-lg border border-border bg-background text-xs"
          data-testid={`vote-comment-${challenge.id}`}
        />
      </div>
      <div className="flex justify-end gap-2">
        {myVote && (
          <Button size="sm" variant="ghost" onClick={removeVote} disabled={saving} className="text-xs text-muted-foreground">
            <Trash2 className="w-3 h-3 mr-1"/> Eliminar mi voto
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid={`vote-submit-${challenge.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1"/> : <Send className="w-3 h-3 mr-1"/>}
          Guardar voto
        </Button>
      </div>
      <NovaVotesHistory votes={challenge.committee_votes || []} criteria={criteria}/>
    </div>
  );
}
